/**
 * Breadcrumb Navigation Component
 *
 * Shows where the current page lives in the information architecture — derived
 * purely from the URL. Always deterministic, always the same for a given route.
 *
 * The root "/prioritization" node is omitted — the sidebar already communicates
 * top-level context.
 */

import { useEffect, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { UI_LABELS } from '../lib/terminology';
import { useSessionStore } from '../stores/prioritization/sessionStore';
import { useDegapProcessStore } from '../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../stores/prioritization/trackStore';
import { useItemsStore } from '../stores/prioritization/itemsStore';

// ---------------------------------------------------------------------------
// Route label mappings (URL segment → human label)
// ---------------------------------------------------------------------------
const routeLabels: Record<string, string> = {
  'prioritization': 'Home',
  'intake': 'Intake',
  'submit': 'Submit an idea',
  'queue': 'Queue',
  'mine': 'My Submissions',
  'triage': 'Triage Queue',
  'shape': 'Shape',
  'shaping': 'Item Work',
  'attributes': 'Attributes',
  'items': 'Items',
  'cycles': 'Cycles',
  'processes': 'Cycles',
  'new': 'New',
  'decide': 'Decide',
  'engage': 'Engage',
  'gather': 'Gather',
  'arrange': 'Arrange',
  'prioritize': 'Prioritize',
  'lists': UI_LABELS.itemPools,
  'monitor': 'Monitor',
  'drift': 'Drift Detection',
  'realignment': 'Realignment',
  'map': 'Focus Map',
  'analytics': 'Analytics',
  'strategy': 'Strategy',
  'objectives': 'Objectives',
  'alignment': 'Alignment',
  'decisions': 'Decision History',
  'settings': 'Settings',
  'users': 'Users',
  'roles': 'Roles',
  'governance': 'Governance',
  'connectors': 'Connectors',
  'security': 'Security',
  'goals': 'Goal Verification',
  'workspace': 'Workspace',
  'admin': 'Admin',
  'documentation': 'Documentation',
  'relationships': 'Relationships',
  'clusters': 'Clusters',
  'packages': 'Packages',
  'exclusives': 'Competing',
  'blocs': 'Packages',
  'dependencies': 'Sequence',
  'prerequisites': 'Sequence',
  'sequence': 'Sequence',
  'aggregations': 'Composition',
  'breakdowns': 'Composition',
  'composition': 'Composition',
  'elaborations': 'Elaborations',
  'reframes': 'Additional Categories',
  'lineage': 'Lineage',
  'reports': 'Reports',
  'custom': 'Custom Reports',
  'execution': 'Cycle Execution',
  'leveling': 'Altitude',
  'types': 'Item Classes',
  'readiness': 'Readiness',
  'scoring': 'Values',
  'collections': 'Collections',
  'priorities': 'Item Pools',
  'plans': 'Plans of Record',
  'tools': 'Tools',
  'exercises': 'Exercises',
  'sessions': 'Sessions',
  'tracks': 'Tracks',
  'overview': 'Overview',
  'catchball': 'Catchball',
  'workbench': 'Loop workspace',
  'my-tasks': 'My work',
  'my-work': 'My work',
  'search': 'Search',
  'features': 'Feature Flags',
  'workflows': 'Workflows',
  'templates': 'Templates',
  'invites': 'Invites',
  'groups': 'Groups',
  'permissions': 'Permissions',
  'policies': 'Policies',
  'queues': 'Queues',
  'org-hierarchy': 'Org Hierarchy',
  'opportunity-trees': 'Opportunity Trees',
  'cycle-design': 'Cycle Design',
  'proposals': 'Proposals',
  'ratification': 'Ratification',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface BreadcrumbItem {
  label: string;
  href: string;
  current: boolean;
  /** Optional type label shown as a small prefix (e.g. "Cycle", "Track", "Session") */
  typeTag?: string;
}

const UUID_RE = /^[0-9a-f-]{36}$/i;
const LONG_ID_RE = /^[a-z0-9]{20,}$/i;

function isUuid(seg: string) {
  return UUID_RE.test(seg) || LONG_ID_RE.test(seg);
}

/** Labels that depend on the segment before: items/readiness is the Completeness page. */
const nestedRouteLabels: Record<string, string> = {
  'items/readiness': 'Completeness',
  'settings/definition': 'Readiness',
};

function segmentLabel(seg: string, parent?: string): string {
  const nested = parent ? nestedRouteLabels[`${parent}/${seg}`] : undefined;
  return nested ?? routeLabels[seg] ?? (seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' '));
}

// ---------------------------------------------------------------------------
// IA crumbs — purely URL-derived
// ---------------------------------------------------------------------------
function buildIACrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const meaningful = segments.filter((s) => s !== 'prioritization' && !isUuid(s));
  if (meaningful.length === 0) return [];

  const crumbs: BreadcrumbItem[] = [];
  let hrefCursor = '';
  let parent: string | undefined;
  for (const seg of segments) {
    hrefCursor += '/' + seg;
    if (seg === 'prioritization' || isUuid(seg)) {
      continue;
    }
    crumbs.push({ label: segmentLabel(seg, parent), href: hrefCursor, current: false });
    parent = seg;
  }

  return crumbs.map((c, i) => ({ ...c, current: i === crumbs.length - 1 }));
}

// ---------------------------------------------------------------------------
// Hook — kept for Layout compatibility (no-op now, safe to remove later)
// ---------------------------------------------------------------------------

/** No-op hook kept so Layout.tsx doesn't need a simultaneous change. */
// eslint-disable-next-line react-refresh/only-export-components
export function useBreadcrumbHistoryTracker() {
  const location = useLocation();
  const prevRef = useRef<string | null>(null);
  useEffect(() => {
    prevRef.current = location.pathname;
  }, [location.pathname]);
}

// ---------------------------------------------------------------------------
// Pattern constants
// ---------------------------------------------------------------------------
const SESSION_DETAIL_PATH = /^\/prioritization\/sessions\/([^/]+)$/;
const TRACK_DETAIL_PATH = /^\/prioritization\/tracks\/([^/]+)$/;
const ITEM_DETAIL_PATH = /^\/prioritization\/items\/([^/]+)$/;

// ---------------------------------------------------------------------------
// Breadcrumb / DualBreadcrumb — both render the same IA trail
// ---------------------------------------------------------------------------
export function DualBreadcrumb() {
  const location = useLocation();
  const params = useParams<{ sessionId?: string; trackId?: string; itemId?: string }>();
  const pathname = location.pathname;

  const sessionMatch = pathname.match(SESSION_DETAIL_PATH);
  const sessionId = sessionMatch?.[1] ?? params.sessionId;

  const trackMatch = pathname.match(TRACK_DETAIL_PATH);
  const trackDetailId = trackMatch?.[1] ?? (!sessionMatch ? params.trackId : undefined);

  const itemMatch = pathname.match(ITEM_DETAIL_PATH);
  // Only treat the captured segment as an item ID when it looks like a real ID
  // (UUID or long alphanumeric), not a named sub-route like "readiness" or "types".
  const rawItemSeg = itemMatch?.[1];
  const itemId = (rawItemSeg && isUuid(rawItemSeg) ? rawItemSeg : null) ?? params.itemId ?? null;

  const session = useSessionStore((state) =>
    sessionId ? state.sessions.find((s) => s.id === sessionId) : null
  );

  // For session breadcrumb: look up cycle and track via session
  const sessionCycle = useDegapProcessStore((state) =>
    session ? state.cycles.find((c) => c.id === session.cycleId) : null
  );
  const sessionTrack = useTrackStore((state) =>
    session ? state.tracks.find((t) => t.id === session.trackId) : null
  );

  // For track detail breadcrumb: look up track and its parent cycle
  const trackDetailRecord = useTrackStore((state) =>
    trackDetailId ? state.tracks.find((t) => t.id === trackDetailId) : null
  );
  const trackDetailCycle = useDegapProcessStore((state) =>
    trackDetailRecord ? state.cycles.find((c) => c.id === trackDetailRecord.cycleId) : null
  );

  const item = useItemsStore((state) => (itemId ? state.getItemById(itemId) : null));

  // ── Session detail — Cycle › Track › Session with type labels ──
  if (sessionMatch && sessionId) {
    const crumbs: BreadcrumbItem[] = [
      {
        href: session?.cycleId
          ? `/prioritization/cycles/${session.cycleId}`
          : '/prioritization/cycles',
        label: sessionCycle?.name ?? 'Unnamed Cycle',
        typeTag: 'Cycle',
        current: false,
      },
      ...(session?.trackId
        ? [{
            href: `/prioritization/tracks/${session.trackId}`,
            label: sessionTrack?.name ?? 'Unnamed Track',
            typeTag: 'Track' as const,
            current: false,
          }]
        : []),
      {
        href: pathname,
        label: session?.name ?? 'Session',
        typeTag: 'Session',
        current: true,
      },
    ];
    return <CrumbNav crumbs={crumbs} />;
  }

  // ── Track detail — Cycle › Track with type labels ──
  if (trackMatch && trackDetailId) {
    const crumbs: BreadcrumbItem[] = [
      {
        href: trackDetailRecord?.cycleId
          ? `/prioritization/cycles/${trackDetailRecord.cycleId}`
          : '/prioritization/cycles',
        label: trackDetailCycle?.name ?? 'Unnamed Cycle',
        typeTag: 'Cycle',
        current: false,
      },
      {
        href: pathname,
        label: trackDetailRecord?.name ?? 'Track',
        typeTag: 'Track',
        current: true,
      },
    ];
    return <CrumbNav crumbs={crumbs} />;
  }

  const crumbs = buildIACrumbs(pathname);

  // Resolve item UUID to real title
  if (itemId && item) {
    const title = (item as { title?: string }).title?.trim();
    if (title) {
      const shorten = (t: string) => (t.length > 52 ? t.slice(0, 49) + '…' : t);
      const last = crumbs[crumbs.length - 1];
      if (last && (last.label === 'Item' || last.label === 'Items')) {
        crumbs[crumbs.length - 1] = { ...last, label: shorten(title) };
      }
    }
  }

  if (crumbs.length <= 1) return null;

  return <CrumbNav crumbs={crumbs} />;
}

function CrumbNav({ crumbs }: { crumbs: BreadcrumbItem[] }) {
  return (
    <nav className="flex items-center" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-1 m-0 p-0 list-none">
        {crumbs.map((crumb, index) => (
          <li key={crumb.href} className="flex items-center">
            {index > 0 && (
              <span className="text-[var(--text-ter)] mx-1.5 text-xs select-none" aria-hidden="true">·</span>
            )}
            {crumb.current ? (
              <span
                className="inline-flex items-center gap-1 text-sm font-medium text-[var(--text-sec)] truncate max-w-[240px]"
                aria-current="page"
                title={crumb.label}
              >
                {/* "You Are Here" location pin */}
                <MapPin className="h-2.5 w-2.5 text-primary-400 flex-shrink-0" aria-hidden="true" />
                {crumb.typeTag && (
                  <span className="text-xs font-normal text-[var(--text-ter)] mr-0.5">{crumb.typeTag}:</span>
                )}
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.href}
                className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors truncate max-w-[240px]"
                title={crumb.label}
              >
                {crumb.typeTag && (
                  <span className="text-xs font-normal text-[var(--text-ter)] mr-1">{crumb.typeTag}:</span>
                )}
                {crumb.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function Breadcrumb() {
  return <DualBreadcrumb />;
}

export default Breadcrumb;
